import { Channel } from 'phoenix'
import React, { useState, useEffect, useContext, useRef } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { WebsocketContext } from '~/service/WebsocketConn'

export interface WithWebsocketProps<ApiData> {
  apiData: ApiData
  channel: Channel
}

type Params = {
  [key: string]: any
}

const withWebsocket = <
  ApiData,
  P extends WithWebsocketProps<ApiData>,
  ParentProps = {}
>(
  WrappedComponent: React.FC<P & ParentProps>,
  topic: (routeParams: Params, props: ParentProps) => string
) => {
  const Component = (props: ParentProps) => {
    const { params: routeParams } = useRouteMatch<Params>()
    const [apiData, setApiData] = useState<ApiData>()
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(true)
    const channelRef = useRef<Channel>()
    const ws = useContext(WebsocketContext)

    useEffect(() => {
      const channel = ws.socket.channel(topic(routeParams, props))

      channelRef.current = channel

      channel
        .join()
        .receive('ok', data => {
          setApiData(data)
          setLoading(false)
        })
        .receive('error', data => {
          setError(true)
        })

      return () => {
        channel.leave()
      }
    }, [])

    if (loading) {
      return (
        <div className="loading">
          <span className="fa fa-spin fa-spinner-third" />
        </div>
      )
    }

    if (error) {
      return <>WS Error</>
    }

    return (
      <WrappedComponent
        {...(props as P & ParentProps)}
        apiData={apiData}
        channel={channelRef.current}
      />
    )
  }

  return Component
}

export default withWebsocket
